import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { travelerHomePath, type LiveMatch } from './lib/tripHome';
import { useAuth } from './hooks/useAuth';
import { getCurrentTheme } from './hooks/useTheme';
import { useActiveDesignTheme } from './hooks/useDesignTheme';
import { supabase } from './lib/supabase';
import { initNative } from './lib/native';
import { registerPush } from './lib/device/push';
import { AppLayout } from './components/layout/AppLayout';
import { TripLayout } from './components/layout/TripLayout';
import { HostMatchLayout } from './components/layout/HostMatchLayout';
import { AuthLayout } from './pages/auth/AuthLayout';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { AuthCallback } from './pages/auth/AuthCallback';
import { ConfirmDelete } from './pages/auth/ConfirmDelete';
import { TryChat } from './pages/TryChat';
import { MatchSelection } from './pages/traveler/MatchSelection';
import { HostProfileView } from './pages/traveler/trip/HostProfileView';
import { TripChat } from './pages/traveler/trip/TripChat';
import { HandbookPage } from './pages/traveler/trip/HandbookPage';
import { HostDashboard } from './pages/host/Dashboard';
import { ItineraryEditor } from './pages/host/ItineraryEditor';
import { HostProfileEditor } from './pages/host/HostProfileEditor';
import { Availability } from './pages/host/Availability';
import { EventLibrary } from './pages/host/EventLibrary';
import { PastTrips } from './pages/host/PastTrips';
import { Earnings } from './pages/host/Earnings';
import { HostChat } from './pages/host/HostChat';
import { HostChats } from './pages/host/HostChats';
import { HandbookPreview } from './pages/host/HandbookPreview';
import { DesignSettings } from './pages/admin/Design';
import { AdminUsers } from './pages/admin/Users';
import { AdminTrips } from './pages/admin/Trips';
import { AdminHosts } from './pages/admin/Hosts';
import { AdminTripPreview } from './pages/admin/TripPreview';
import { AdminHostMatching } from './pages/admin/HostMatching';
import { AdminConversations } from './pages/admin/Conversations';
import { AdminPortraits } from './pages/admin/Portraits';
import { AdminCategories } from './pages/admin/Categories';
import { AdminHostSpecialties } from './pages/admin/HostSpecialties';
import { AdminAlmaQuestions } from './pages/admin/AlmaQuestions';
import { AdminAlmaPart2 } from './pages/admin/AlmaPart2';
import { AdminSekretlyMethodology } from './pages/admin/SekretlyMethodology';
import { AdminPricing } from './pages/admin/Pricing';
import { AdminPayments } from './pages/admin/Payments';
import { AdminInvoices } from './pages/admin/Invoices';
import { AdminFAQ } from './pages/admin/FAQ';
import { AdminDestinations } from './pages/admin/Destinations';
import { AdminFeedback } from './pages/admin/Feedback';
import { AdminAIPersonas } from './pages/admin/AIPersonas';
import { AdminLegal } from './pages/admin/Legal';
import { Survey } from './pages/Survey';
import { AdminSurveys } from './pages/admin/Surveys';
import { AdminAlmaUsage } from './pages/admin/AlmaUsage';
import { AdminPlaceRatings } from './pages/admin/PlaceRatings';
import { AdminPushBroadcast } from './pages/admin/PushBroadcast';
import { AdminAffiliates } from './pages/admin/Affiliates';
import { QrCapture } from './pages/QrCapture';
import { Payment } from './pages/traveler/Payment';
import { PaymentSuccess } from './pages/traveler/PaymentSuccess';
import { PaymentCancel } from './pages/traveler/PaymentCancel';
import { ComingSoon } from './pages/admin/ComingSoon';
import { ADMIN_ITEMS } from './lib/adminNav';
import { Settings } from './pages/Settings';
import { TermsPage } from './pages/legal/Terms';
import { HostAgreementPage } from './pages/legal/HostAgreement';
import { NotFound } from './pages/NotFound';
import { AuthProvider } from './components/AuthProvider';
import { ContentWidthProvider } from './hooks/useContentWidth';
import { Spinner, useLoadIndicator } from './components/design-system';
import type { UserRole } from './types';
import loadingLaunchOrangeUrl from './components/design-system/feedback/assets/loading-launch-orange.gif';

const ROLE_HOME: Record<UserRole, string> = {
  traveler: '/home',
  host: '/host',
  admin: '/admin',
};

// The cold-launch moment only — every caller of useLoadIndicator's 'screen'
// variant is, by construction, the one-per-session first load (see that
// hook's doc comment), so this is never shown for a later in-app load. Sits
// on the brand orange (#FF8D23) rather than the app's normal theme
// background, so it reads as a continuation of the native splash rather
// than a new screen — the native splash's own image is this same animation's
// first frame. This is a GIF, not a <video>, deliberately: a <video> here
// hit a device where autoplay was blocked (likely the OS's "Auto-Play Video
// Previews" motion setting), leaving native play/pause chrome stuck over the
// loader. A GIF is just an image to the OS, so it always plays regardless of
// that policy.
function FullScreenSpinner() {
  return (
    <div
      className="flex items-center justify-center"
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: '#FF8D23' }}
    >
      <img src={loadingLaunchOrangeUrl} alt="" style={{ width: 120, height: 120 }} />
    </div>
  );
}

function RoleRedirect() {
  const { user, activeRole, loading } = useAuth();
  const showLoader = useLoadIndicator(loading) === 'screen';

  // Email confirmation / recovery links built from the project's Site URL land
  // here at the root carrying the auth params. Redirecting away would drop
  // them and silently leave the account unconfirmed, so hand them to the
  // callback route that knows how to complete the exchange.
  const search = window.location.search;
  const params = new URLSearchParams(search);
  if (params.has('code') || params.has('token_hash') || params.has('error_description')) {
    return <Navigate to={`/auth/callback${search}`} replace />;
  }

  if (showLoader) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/try" replace />;
  return <Navigate to={ROLE_HOME[activeRole]} replace />;
}

/**
 * TravelerHome — resolves a traveler to the right place in their journey:
 * a live trip (pending/accepted) → its chat; unpaid proposals → host choice;
 * a finished trip → that trip's profile; nothing yet → the Alma chat at /try.
 */
function TravelerHome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const loadVariant = useLoadIndicator(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/try', { replace: true }); return; }
    let cancelled = false;
    void (async () => {
      const { data: matches } = await supabase
        .from('matches')
        .select('id, status, created_at, leg:trip_legs(start_date, days, position)')
        .eq('traveler_id', user.id)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      const rows = (matches ?? []) as unknown as LiveMatch[];
      navigate(travelerHomePath(rows, new Date().toISOString().slice(0, 10)), { replace: true });
    })();
    return () => { cancelled = true; };
  }, [user, loading, navigate]);

  if (loadVariant === 'screen') return <FullScreenSpinner />;
  return (
    <div className="min-h-dvh flex items-center justify-center bg-app-bg">
      <Spinner />
    </div>
  );
}

interface ProtectedRouteProps {
  children: JSX.Element;
  requiredRole?: UserRole;
  hideSidebar?: boolean;
  fullBleed?: boolean;
  showTabBar?: boolean;
}

function ProtectedRoute({ children, requiredRole, hideSidebar, fullBleed, showTabBar }: ProtectedRouteProps) {
  const { user, activeRole, loading } = useAuth();
  const navigate = useNavigate();
  const showLoader = useLoadIndicator(loading) === 'screen';
  // Locked to the role in use, not every role the account holds: an admin
  // acting as a host must not reach admin pages (and vice versa).
  const wrongRole = !!requiredRole && activeRole !== requiredRole;

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (wrongRole) navigate(ROLE_HOME[activeRole], { replace: true });
  }, [user, activeRole, loading, navigate, wrongRole]);

  if (showLoader) return <FullScreenSpinner />;
  // Never flash a page the active role can't use while the redirect runs.
  if (!loading && user && wrongRole) return <FullScreenSpinner />;

  return (
    <AppLayout hideSidebar={hideSidebar} fullBleed={fullBleed} showTabBar={showTabBar}>
      {children}
    </AppLayout>
  );
}

/** Trip routes use their own contextual sidebar (no AppLayout). */
function TripRoute({ children, fullBleed }: { children: JSX.Element; fullBleed?: boolean }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const showLoader = useLoadIndicator(loading) === 'screen';

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/login', { replace: true });
  }, [user, loading, navigate]);

  if (showLoader) return <FullScreenSpinner />;
  return <TripLayout fullBleed={fullBleed}>{children}</TripLayout>;
}

/** Host match routes use their own contextual sidebar (no AppLayout). */
function HostMatchRoute({ children, fullBleed }: { children: JSX.Element; fullBleed?: boolean }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const showLoader = useLoadIndicator(loading) === 'screen';

  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/login', { replace: true });
  }, [user, loading, navigate]);

  if (showLoader) return <FullScreenSpinner />;
  return <HostMatchLayout fullBleed={fullBleed}>{children}</HostMatchLayout>;
}

const DESIGN_TOKEN = import.meta.env.VITE_DESIGN_TOKEN as string | undefined;

function TokenDesign() {
  const { token } = useParams<{ token: string }>();
  if (!DESIGN_TOKEN || token !== DESIGN_TOKEN) {
    return <NotFound />;
  }
  return <DesignSettings />;
}

function NativeServices() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    let cleanup: (() => void) | undefined;
    void registerPush({
      // A tap on a push carrying a path (e.g. the trip survey) opens that screen.
      onActionTap: (data) => {
        const url = data.url;
        if (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')) navigate(url);
      },
    }).then((fn) => { cleanup = fn; });
    return () => cleanup?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Global recovery-session listener — the one mechanism that works
  // regardless of platform or which page happens to be mounted when the
  // link is opened. AuthCallback's own type=recovery check (web only)
  // already routes there immediately from the URL, avoiding a home-screen
  // flash first; on native the deep link is handled entirely inside
  // lib/native.ts's handleDeepLink, which never mounts AuthCallback or
  // parses `type` at all, so this is the ONLY thing that catches it there.
  // PASSWORD_RECOVERY fires from Supabase's own auth state machine whenever
  // a recovery session is established, however that happened.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') navigate('/reset-password', { replace: true });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  return null;
}

export default function App() {
  useActiveDesignTheme();

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    void initNative(getCurrentTheme).then((fn) => { cleanup = fn; });
    return () => cleanup?.();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
      <ContentWidthProvider>
      <NativeServices />
      <Routes>
        <Route path="/" element={<RoleRedirect />} />
        <Route path="/try" element={<TryChat />} />
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Route>
        {/* Legacy chooser/form split — collapsed back into one screen per mode. */}
        <Route path="/login/email" element={<Navigate to="/login" replace />} />
        <Route path="/register/email" element={<Navigate to="/register" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/confirm-delete" element={<ConfirmDelete />} />
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/host-agreement" element={<HostAgreementPage />} />
        <Route path="/qrc" element={<QrCapture />} />
        <Route path="/survey/:token" element={<Survey />} />

        {/* Traveler routes */}
        <Route path="/home" element={<TravelerHome />} />
        <Route path="/matches" element={<ProtectedRoute requiredRole="traveler" hideSidebar fullBleed><MatchSelection /></ProtectedRoute>} />
        <Route path="/payment" element={<ProtectedRoute requiredRole="traveler" hideSidebar fullBleed><Payment /></ProtectedRoute>} />
        <Route path="/payment/success" element={<ProtectedRoute requiredRole="traveler" hideSidebar fullBleed><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/payment/cancel" element={<ProtectedRoute requiredRole="traveler"><PaymentCancel /></ProtectedRoute>} />
        {/* Legacy /trip redirect */}
        <Route path="/trip" element={<Navigate to="/home" replace />} />

        {/* Trip-contextual routes — own sidebar layout */}
        <Route path="/trip/:id/profile" element={<TripRoute><HostProfileView /></TripRoute>} />
        <Route path="/trip/:id/chat" element={<TripRoute fullBleed><TripChat /></TripRoute>} />
        <Route path="/trip/:id/handbook" element={<TripRoute><HandbookPage /></TripRoute>} />
        <Route path="/trip/:id" element={<Navigate to="profile" replace />} />

        {/* Host routes */}
        <Route path="/host" element={<ProtectedRoute requiredRole="host"><HostDashboard /></ProtectedRoute>} />
        <Route path="/host/itinerary/:matchId" element={<HostMatchRoute fullBleed><ItineraryEditor /></HostMatchRoute>} />
        <Route path="/host/preview/:matchId" element={<HostMatchRoute><HandbookPreview /></HostMatchRoute>} />
        <Route path="/host/chat/:matchId" element={<HostMatchRoute fullBleed><HostChat /></HostMatchRoute>} />
        <Route path="/host/chats" element={<ProtectedRoute requiredRole="host" fullBleed><HostChats /></ProtectedRoute>} />
        <Route path="/host/chats/:matchId" element={<ProtectedRoute requiredRole="host" fullBleed><HostChats /></ProtectedRoute>} />
        <Route path="/host/profile" element={<ProtectedRoute requiredRole="host"><HostProfileEditor /></ProtectedRoute>} />
        <Route path="/host/availability" element={<ProtectedRoute requiredRole="host"><Availability /></ProtectedRoute>} />
        <Route path="/host/past" element={<ProtectedRoute requiredRole="host"><PastTrips /></ProtectedRoute>} />
        <Route path="/host/library" element={<ProtectedRoute requiredRole="host"><EventLibrary /></ProtectedRoute>} />
        <Route path="/host/earnings" element={<ProtectedRoute requiredRole="host"><Earnings /></ProtectedRoute>} />

        {/* Admin routes */}
        <Route path="/admin" element={<Navigate to="/admin/trips" replace />} />
        <Route path="/admin/users" element={<ProtectedRoute requiredRole="admin"><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/trips" element={<ProtectedRoute requiredRole="admin"><AdminTrips /></ProtectedRoute>} />
        <Route path="/admin/hosts" element={<ProtectedRoute requiredRole="admin"><AdminHosts /></ProtectedRoute>} />
        <Route path="/admin/trips/:id" element={<ProtectedRoute requiredRole="admin"><AdminTripPreview /></ProtectedRoute>} />
        <Route path="/admin/host-matching" element={<ProtectedRoute requiredRole="admin"><AdminHostMatching /></ProtectedRoute>} />
        <Route path="/admin/conversations" element={<ProtectedRoute requiredRole="admin"><AdminConversations /></ProtectedRoute>} />
        <Route path="/admin/portraits" element={<ProtectedRoute requiredRole="admin"><AdminPortraits /></ProtectedRoute>} />
        <Route path="/admin/design" element={<ProtectedRoute requiredRole="admin"><DesignSettings /></ProtectedRoute>} />
        <Route path="/design/:token" element={<TokenDesign />} />
        <Route path="/admin/categories" element={<ProtectedRoute requiredRole="admin"><AdminCategories /></ProtectedRoute>} />
        <Route path="/admin/host-specialties" element={<ProtectedRoute requiredRole="admin"><AdminHostSpecialties /></ProtectedRoute>} />
        <Route path="/admin/alma-questions" element={<ProtectedRoute requiredRole="admin"><AdminAlmaQuestions /></ProtectedRoute>} />
        <Route path="/admin/alma-part2" element={<ProtectedRoute requiredRole="admin"><AdminAlmaPart2 /></ProtectedRoute>} />
        <Route path="/admin/pricing" element={<ProtectedRoute requiredRole="admin"><AdminPricing /></ProtectedRoute>} />
        <Route path="/admin/payments" element={<ProtectedRoute requiredRole="admin"><AdminPayments /></ProtectedRoute>} />
        <Route path="/admin/invoices" element={<ProtectedRoute requiredRole="admin"><AdminInvoices /></ProtectedRoute>} />
        <Route path="/admin/affiliates" element={<ProtectedRoute requiredRole="admin"><AdminAffiliates /></ProtectedRoute>} />
        <Route path="/admin/faq" element={<ProtectedRoute requiredRole="admin"><AdminFAQ /></ProtectedRoute>} />
        <Route path="/admin/destinations" element={<ProtectedRoute requiredRole="admin"><AdminDestinations /></ProtectedRoute>} />
        <Route path="/admin/feedback" element={<ProtectedRoute requiredRole="admin"><AdminFeedback /></ProtectedRoute>} />
        <Route path="/admin/ai-personas" element={<ProtectedRoute requiredRole="admin"><AdminAIPersonas /></ProtectedRoute>} />
        <Route path="/admin/methodology" element={<ProtectedRoute requiredRole="admin"><AdminSekretlyMethodology /></ProtectedRoute>} />
        <Route path="/admin/legal" element={<ProtectedRoute requiredRole="admin"><AdminLegal /></ProtectedRoute>} />
        <Route path="/admin/surveys" element={<ProtectedRoute requiredRole="admin"><AdminSurveys /></ProtectedRoute>} />
        <Route path="/admin/alma-usage" element={<ProtectedRoute requiredRole="admin"><AdminAlmaUsage /></ProtectedRoute>} />
        <Route path="/admin/place-ratings" element={<ProtectedRoute requiredRole="admin"><AdminPlaceRatings /></ProtectedRoute>} />
        <Route path="/admin/push" element={<ProtectedRoute requiredRole="admin"><AdminPushBroadcast /></ProtectedRoute>} />
        {ADMIN_ITEMS.filter((item) => item.comingSoon).map((item) => (
          <Route
            key={item.to}
            path={item.to}
            element={<ProtectedRoute requiredRole="admin"><ComingSoon title={item.label} /></ProtectedRoute>}
          />
        ))}

        {/* Shared */}
        <Route path="/settings" element={<ProtectedRoute showTabBar><Settings /></ProtectedRoute>} />

        <Route path="*" element={<NotFound />} />
      </Routes>
      </ContentWidthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
